import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Typography,
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    Alert,
    Stack,
    Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';

function CartPage() {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [debug, setDebug] = useState('');

    // Load cart from localStorage
    const loadCart = () => {
        try {
            const cartData = localStorage.getItem('cart');
            setDebug(`Raw cart data: ${cartData}`);
            
            if (cartData) {
                const cart = JSON.parse(cartData);
                setCartItems(cart);
                setDebug(`Loaded ${cart.length} items`);
            } else {
                setCartItems([]);
                setDebug('No cart data found');
            }
        } catch (error) {
            console.error('Error loading cart:', error);
            setDebug(`Error: ${error.message}`);
            setCartItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCart();
    }, []);

    // ✅ FIXED: Save cart and dispatch event to update navbar
    const saveCart = (items) => {
        try {
            localStorage.setItem('cart', JSON.stringify(items));
            setCartItems(items);
            setDebug(`Saved ${items.length} items to cart`);
            
            // ✅ Dispatch event to update navbar badge
            window.dispatchEvent(new CustomEvent('cartUpdated'));
            
        } catch (error) {
            console.error('Error saving cart:', error);
            setDebug(`Save error: ${error.message}`);
        }
    };

    // Update quantity
    const updateQuantity = (productId, change) => {
        const updatedCart = cartItems.map(item => {
            if (item.id === productId) {
                const newQuantity = Math.max(1, (item.quantity || 1) + change);
                return { ...item, quantity: newQuantity };
            }
            return item;
        });
        saveCart(updatedCart);
    };

    // Remove item from cart
    const removeItem = (productId) => {
        const updatedCart = cartItems.filter(item => item.id !== productId);
        saveCart(updatedCart);
    };

    // Clear entire cart
    const clearCart = () => {
        if (window.confirm('Are you sure you want to clear your cart?')) {
            saveCart([]);
        }
    };

    // Calculate totals
    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            const price = item.price || 0;
            const quantity = item.quantity || 1;
            return total + (price * quantity);
        }, 0);
    };

    const totalItems = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const totalPrice = calculateTotal();

    // Handle WhatsApp order
    const handleWhatsAppOrder = () => {
        if (cartItems.length === 0) return;

        let message = 'Hello, I would like to order the following items:\n\n';
        cartItems.forEach((item, index) => {
            const title = item.title || item.name || 'Product';
            const quantity = item.quantity || 1;
            message += `${index + 1}. ${title} x ${quantity}`;
            if (item.price) {
                message += ` - KES ${(item.price * quantity).toLocaleString()}`;
            }
            message += '\n';
        });
        message += `\nTotal: KES ${totalPrice.toLocaleString()}`;
        message += '\n\nPlease confirm my order.';

        window.open(`https://wa.me/254700000000?text=${encodeURIComponent(message)}`, '_blank');
    };

    // Add a test item
    const addTestItem = () => {
        const testItem = {
            id: Date.now(),
            title: "Test Product",
            price: 99.99,
            image: "/static/images/products/1000450438.jpg",
            quantity: 1
        };
        const newCart = [...cartItems, testItem];
        saveCart(newCart);
    };

    // Show loading state
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
                <Typography>Loading cart...</Typography>
            </Container>
        );
    }

    // Empty cart
    if (cartItems.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, minHeight: '60vh' }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Paper
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 3,
                            bgcolor: '#f8f9fa'
                        }}
                    >
                        <ShoppingCartIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                            Your Cart is Empty
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            Looks like you haven't added any items to your cart yet.
                            <br />
                            Browse our products and find something you love!
                        </Typography>
                        
                        {/* Debug info */}
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f0fe', borderRadius: 2, textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Debug: {debug}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                localStorage cart: {localStorage.getItem('cart') || 'null'}
                            </Typography>
                        </Box>

                        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/')}
                                sx={{
                                    bgcolor: '#667eea',
                                    '&:hover': { bgcolor: '#5a6fd6' },
                                    px: 4,
                                    py: 1.5
                                }}
                            >
                                Start Shopping
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                    loadCart();
                                    window.dispatchEvent(new CustomEvent('cartUpdated'));
                                }}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                Refresh Cart
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                color="primary"
                                onClick={addTestItem}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                Add Test Item
                            </Button>
                        </Box>
                    </Paper>
                </motion.div>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4, minHeight: '60vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate('/')} sx={{ color: '#667eea' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            Shopping Cart
                        </Typography>
                        <Chip label={`${totalItems} items`} size="small" color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={clearCart}
                            sx={{ borderRadius: 50 }}
                        >
                            Clear Cart
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                loadCart();
                                window.dispatchEvent(new CustomEvent('cartUpdated'));
                            }}
                            sx={{ borderRadius: 50 }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                {/* Debug info */}
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e8f0fe', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Debug: {debug} | Items: {cartItems.length} | Cart Data: {JSON.stringify(cartItems).substring(0, 100)}...
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Cart Items */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            {cartItems.map((item, index) => (
                                <motion.div
                                    key={item.id || index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            py: 2,
                                            borderBottom: index < cartItems.length - 1 ? '1px solid #f0f0f0' : 'none',
                                            flexWrap: 'wrap',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {/* Image */}
                                        <Box
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                bgcolor: '#f5f5f5',
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <img
                                                src={item.image || '/placeholder-image.jpg'}
                                                alt={item.title || item.name || 'Product'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => {
                                                    e.target.src = '/placeholder-image.jpg';
                                                }}
                                            />
                                        </Box>

                                        {/* Details */}
                                        <Box sx={{ flex: 1, minWidth: 120 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                                {item.title || item.name || 'Product'}
                                            </Typography>
                                            {item.price !== null && item.price !== undefined ? (
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                                                    KES {Number(item.price).toLocaleString()}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    Contact for Price
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Quantity Controls */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => updateQuantity(item.id, -1)}
                                                sx={{ border: '1px solid #ddd', borderRadius: 1 }}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                                                {item.quantity || 1}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => updateQuantity(item.id, 1)}
                                                sx={{ border: '1px solid #ddd', borderRadius: 1 }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* Subtotal & Delete */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 100, justifyContent: 'flex-end' }}>
                                            {item.price !== null && item.price !== undefined ? (
                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                    KES {(Number(item.price) * (item.quantity || 1)).toLocaleString()}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Contact for price
                                                </Typography>
                                            )}
                                            <IconButton
                                                onClick={() => removeItem(item.id)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </Paper>
                    </Grid>

                    {/* Summary */}
                    <Grid item xs={12} md={4}>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 20 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Order Summary
                                </Typography>

                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography color="text.secondary">Items ({totalItems})</Typography>
                                        <Typography>KES {totalPrice.toLocaleString()}</Typography>
                                    </Box>

                                    <Divider />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            Total
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                                            KES {totalPrice.toLocaleString()}
                                        </Typography>
                                    </Box>

                                    {totalPrice === 0 && cartItems.some(item => item.price === null) && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Some items have no price. Please contact us for pricing.
                                        </Alert>
                                    )}

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        startIcon={<WhatsAppIcon />}
                                        onClick={handleWhatsAppOrder}
                                        sx={{
                                            bgcolor: '#25D366',
                                            '&:hover': { bgcolor: '#1DA851' },
                                            py: 1.5,
                                            borderRadius: 50,
                                            fontWeight: 'bold',
                                            mt: 2
                                        }}
                                    >
                                        Order via WhatsApp
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => navigate('/')}
                                        sx={{ borderRadius: 50 }}
                                    >
                                        Continue Shopping
                                    </Button>
                                </Stack>
                            </Paper>
                        </motion.div>
                    </Grid>
                </Grid>
            </motion.div>
        </Container>
    );
}

export default CartPage;